import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import { fmtCost } from '../utils/format';

// At or above this, a candidate is worth assessing deeper before voiding.
const MATCH_PCT = 80;

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland', day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

// Same button as Margin Check -- app-header__btn is invisible on white.
const primaryBtn = (disabled) => ({
  background: disabled ? '#9CA3AF' : '#1B2A4A',
  color:      '#FFFFFF',
  border:     'none',
  padding:    '7px 18px',
  borderRadius: 6,
  fontWeight: 600,
  fontSize:   13,
  cursor:     disabled ? 'not-allowed' : 'pointer',
});

// ── Searched PO ───────────────────────────────────────────────────────────────
const SourceCard = ({ src }) => {
  // Only an approved, unreceived PO is a void candidate.
  const warning = src.status === 'VOID'
    ? 'Already voided in Cin7.'
    : src.received || src.stage !== 'New'
      ? `Stage is "${src.stage || 'blank'}"${src.received ? `, received ${fmtDate(src.received)}` : ''} — this is not an open order PO, don't void it.`
      : null;

  return (
    <div style={{
      margin: '0 16px 12px', padding: '10px 16px', borderRadius: 6,
      background: warning ? '#FEF3C7' : '#F3F4F6',
      border: `1px solid ${warning ? '#FDE68A' : '#E5E7EB'}`,
      fontSize: 13,
    }}>
      <strong>{src.order_ref}</strong> · {src.supplier} · {src.stage} / {src.status}
      {' · '}created {fmtDate(src.created)} · {fmtCost(src.total)} · {src.lines} item code(s)
      {src.comments && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{src.comments}</div>}
      {warning && <div style={{ fontWeight: 600, marginTop: 4 }}>{warning}</div>}
    </div>
  );
};

// ── Candidate families ────────────────────────────────────────────────────────
const CandidateTable = ({ rows, srcTotal }) => {
  const [open, setOpen] = useState({});

  if (!rows.length) {
    return <div className="empty-row">No newer PO from this supplier (within 90 days) shares any item code.</div>;
  }

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th></th>
            <th>PO(s)</th>
            <th>Created</th>
            <th>Invoice Ref</th>
            <th className="num">Total</th>
            <th className="num">Matched</th>
            <th className="num">Match %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const strong = r.pct >= MATCH_PCT;
            const isOpen = !!open[r.family];
            // Receipt splits (A/B/...) are summed, so an exact match on the
            // order PO's total is a strong second signal alongside codes.
            const sameTotal = srcTotal != null && Math.abs(Number(r.total) - Number(srcTotal)) < 0.01;
            return (
              <React.Fragment key={r.family}>
                <tr className="data-row" style={strong ? { background: '#DCFCE7' } : undefined}>
                  <td>
                    <button
                      className="action-btn action-btn--ghost"
                      style={{ fontSize: 10, padding: '2px 10px' }}
                      onClick={() => setOpen((o) => ({ ...o, [r.family]: !o[r.family] }))}
                    >
                      {isOpen ? 'Hide codes' : 'Show codes'}
                    </button>
                  </td>
                  <td>
                    {r.members.map((m) => (
                      <div key={m.order_ref}>
                        <strong>{m.order_ref}</strong>
                        <span style={{ fontSize: 11, color: '#666' }}>
                          {' '}{m.stage}{m.received ? ` · rec'd ${fmtDate(m.received)}` : ''} · {fmtCost(m.total)}
                        </span>
                      </div>
                    ))}
                  </td>
                  <td>{fmtDate(r.first_created)}</td>
                  <td>{r.invoice_refs || '—'}</td>
                  <td className="num">
                    {fmtCost(r.total)}
                    {sameTotal && <div style={{ fontSize: 10, color: '#15803D', fontWeight: 600 }}>same total</div>}
                  </td>
                  <td className="num">{r.matched} / {r.src_lines}</td>
                  <td className="num" style={{ fontWeight: 700, color: strong ? '#15803D' : undefined }}>
                    {r.pct}%
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td></td>
                    <td colSpan={6} style={{ fontSize: 12 }}>
                      <div><strong style={{ color: '#15803D' }}>Matched:</strong> {r.matched_codes.join(', ') || '—'}</div>
                      <div><strong style={{ color: '#B91C1C' }}>Missing:</strong> {r.missing_codes.join(', ') || '—'}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const POMatchSearch = () => {
  const navigate = useNavigate();
  const [term, setTerm]         = useState('');
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const search = useCallback(async () => {
    if (!term.trim()) return;
    setLoading(true);
    setError('');
    const { data, error: rpcError } = await supabase.rpc('find_received_po_matches', { p_po_ref: term.trim() });
    if (rpcError) {
      setError(rpcError.message);
      setResult(null);
    } else {
      setResult(data);
    }
    setLoading(false);
  }, [term]);

  const candidates = result?.candidates || [];
  const strongCount = candidates.filter((c) => c.pct >= MATCH_PCT).length;

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">PO Match Search</span>
      </div>

      <section className="section">
        <SectionHeader
          title="PO Match Search"
          count={result ? candidates.length : null}
          onRefresh={search}
          loading={loading}
        />
        <p className="section__desc">
          Enter an order PO to find newer POs from the same supplier (within 90 days, voided POs
          excluded) that look like its matched invoice / received PO. Match % = item codes on the
          searched PO found on the candidate ÷ item codes on the searched PO. Receipt splits
          (PO-388621, PO-388621A, PO-388621B…) are scored together as one. {MATCH_PCT}%+ is highlighted
          as worth assessing — voiding is done manually in Cin7.
        </p>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 16px 12px' }}>
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder="e.g. PO-387821 or 387821"
            style={{ padding: '6px 10px', fontSize: 12, minWidth: 260 }}
          />
          <button onClick={search} disabled={loading || !term.trim()} style={primaryBtn(loading || !term.trim())}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {result?.source && (
            <span style={{ fontSize: 12, color: strongCount ? '#15803D' : '#888', fontWeight: strongCount ? 600 : 400 }}>
              {strongCount ? `${strongCount} candidate(s) at ${MATCH_PCT}%+` : `No candidate at ${MATCH_PCT}%+`}
            </span>
          )}
        </div>

        {error && <div className="empty-row" style={{ color: '#c00' }}>{error}</div>}

        {loading && <div className="loading-row">Searching...</div>}
        {!loading && !result && !error && <div className="empty-row">Enter a PO number above to begin.</div>}
        {!loading && result && !result.source && <div className="empty-row">No PO found with that number.</div>}
        {!loading && result?.source && (
          <>
            <SourceCard src={result.source} />
            <CandidateTable key={result.source.order_ref} rows={candidates} srcTotal={result.source.total} />
          </>
        )}
      </section>
    </main>
  );
};

export default POMatchSearch;
